import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, ImagePlus, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fcfa } from "@/lib/format";
import AiTextButton from "@/components/admin/AiTextButton";

const inputClass =
  "h-11 w-full rounded-md border border-clay bg-white px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const ImagePicker = ({ value, onChange, testid }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`${testid}-file`} onChange={onFile} />
      <div className="h-20 w-16 shrink-0 overflow-hidden border border-clay bg-white">
        {value ? (
          <img src={value} alt="Aperçu" data-testid={`${testid}-preview`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-clay">
            <ImagePlus className="h-5 w-5" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex h-11 items-center justify-center gap-2 rounded-full bg-bark text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {uploading ? "Envoi en cours…" : value ? "Changer l'image" : "Télécharger une image"}
        </button>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…ou collez un lien d'image (https://)"
          className={inputClass}
          data-testid={`${testid}-url`}
        />
      </div>
      {value && (
        <button
          type="button"
          data-testid={`${testid}-clear`}
          onClick={() => onChange("")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-destructive hover:text-destructive"
          aria-label="Retirer l'image"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

const SectionCard = ({ title, desc, enabled, onToggle, testid, children }) => (
  <div data-testid={testid} className="border border-clay bg-cream">
    <div className="flex items-center justify-between border-b border-clay px-6 py-4">
      <div>
        <h2 className="font-display text-xl font-semibold text-bark">{title}</h2>
        <p className="mt-0.5 text-xs text-bark/50">{desc}</p>
      </div>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={onToggle}
        className={`flex h-9 items-center gap-2 rounded-full px-4 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
          enabled ? "bg-terracotta text-sand" : "border border-clay text-bark/50 hover:border-terracotta hover:text-terracotta"
        }`}
      >
        {enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {enabled ? "Visible" : "Masquée"}
      </button>
    </div>
    {enabled && <div className="space-y-4 p-6">{children}</div>}
  </div>
);

const labelClass = "mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta";

const AiLabel = ({ children, ai }) => (
  <div className="mb-2 flex items-center justify-between gap-3">
    <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">{children}</label>
    {ai}
  </div>
);

const HomeSettings = ({ products }) => {
  const [cfg, setCfg] = useState(null);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickProduct, setPickProduct] = useState("");

  useEffect(() => {
    api.get("/settings/home").then((r) => setCfg(r.data)).catch(() => toast.error("Impossible de charger la configuration de l'accueil"));
    api.get("/categories").then((r) => setCategories(r.data.map((c) => c.name))).catch(() => {});
  }, []);

  if (!cfg) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
      </div>
    );
  }

  const setSection = (key, patch) => setCfg((c) => ({ ...c, [key]: { ...c[key], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/home", cfg);
      setCfg(data);
      toast.success("Page d'accueil enregistrée — visible sur le site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fp = cfg.featured_products;
  const fc = cfg.featured_categories;
  const mf = cfg.manifesto;
  const qb = cfg.quote_band;
  const rv = cfg.reviews || { enabled: true, title: "" };
  const pr = cfg.promo || { enabled: false, title: "", subtitle: "", end_at: "" };
  const selectedProducts = fp.product_ids.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const availableProducts = products.filter((p) => !fp.product_ids.includes(p.id));

  return (
    <section data-testid="admin-home-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-bark">Page d'accueil</h1>
          <p className="mt-1 text-sm text-bark/50">
            Composez l'accueil section par section. Seules les sections « Visible » s'affichent sur le site.
          </p>
        </div>
        <button
          data-testid="admin-home-save"
          onClick={save}
          disabled={saving}
          className="flex h-11 items-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer
        </button>
      </div>

      <div className="mt-8 space-y-6">
        {/* HERO */}
        <SectionCard
          title="Hero (haut de page)"
          desc="Le grand titre, le texte de présentation et l'image principale."
          enabled={cfg.hero.enabled}
          onToggle={() => setSection("hero", { enabled: !cfg.hero.enabled })}
          testid="home-section-hero"
        >
          <div>
            <label className={labelClass}>Sur-titre (petit texte au-dessus du titre)</label>
            <input value={cfg.hero.eyebrow} onChange={(e) => setSection("hero", { eyebrow: e.target.value })} placeholder="Ex : Sama-Keur — Dakar, Sénégal" className={inputClass} data-testid="home-hero-eyebrow-input" />
          </div>
          <div>
            <AiLabel ai={<AiTextButton kind="home_hero_title" testid="home-hero-title-ai-button" context={() => cfg.hero.title} onGenerated={(t) => setSection("hero", { title: t })} />}>Titre (une ligne par saut de ligne — la dernière ligne est mise en accent)</AiLabel>
            <textarea value={cfg.hero.title} onChange={(e) => setSection("hero", { title: e.target.value })} placeholder={"Ex :\nL'élégance\nde votre\nmaison."} className="min-h-[90px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none" data-testid="home-hero-title-input" />
          </div>
          <div>
            <AiLabel ai={<AiTextButton kind="home_hero_text" testid="home-hero-text-ai-button" context={() => cfg.hero.text} onGenerated={(t) => setSection("hero", { text: t })} />}>Texte de présentation</AiLabel>
            <textarea value={cfg.hero.text} onChange={(e) => setSection("hero", { text: e.target.value })} placeholder="Ex : Robinetterie, meubles de salle de bain et lits coffres — livrés en 48h partout à Dakar." className="min-h-[70px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none" data-testid="home-hero-text-input" />
          </div>
          <div>
            <label className={labelClass}>Produit Best-Seller (affiché sur l'image — facultatif)</label>
            <select
              value={cfg.hero.best_seller_id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const prod = products.find((p) => p.id === id);
                setSection("hero", { best_seller_id: id, ...(prod ? { image: prod.image } : {}) });
              }}
              className={inputClass}
              data-testid="home-hero-bestseller-select"
            >
              <option value="">Aucun</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name} — {fcfa(p.price)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-bark/40">Choisir un produit utilise automatiquement sa photo comme image du hero.</p>
          </div>
          <div>
            <label className={labelClass}>Image du hero (facultatif)</label>
            <ImagePicker value={cfg.hero.image} onChange={(url) => setSection("hero", { image: url })} testid="home-hero-image" />
          </div>
        </SectionCard>

        {/* PROMOTION — page /promo */}
        <SectionCard
          title="Promotion — page /promo"
          desc="Bannière et compte à rebours affichés en haut de la page /promo (Lits coffres & cuisine)."
          enabled={pr.enabled}
          onToggle={() => setSection("promo", { enabled: !pr.enabled })}
          testid="home-section-promo"
        >
          <div>
            <label className={labelClass}>Titre de la promotion</label>
            <input value={pr.title || ""} onChange={(e) => setSection("promo", { title: e.target.value })} placeholder="Ex : Teranga Days — lits coffres & cuisine à prix doux" className={inputClass} data-testid="home-promo-title-input" />
          </div>
          <div>
            <label className={labelClass}>Sous-titre</label>
            <textarea value={pr.subtitle || ""} onChange={(e) => setSection("promo", { subtitle: e.target.value })} placeholder="Ex : Jusqu'à dimanche minuit, sur toute la sélection." className="min-h-[60px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none" data-testid="home-promo-subtitle-input" />
          </div>
          <div>
            <label className={labelClass}>Fin de la promotion (déclenche le compte à rebours)</label>
            <input type="datetime-local" value={pr.end_at || ""} onChange={(e) => setSection("promo", { end_at: e.target.value })} className={inputClass} data-testid="home-promo-end-input" />
          </div>
        </SectionCard>

        {/* PRODUITS MIS EN AVANT */}
        <SectionCard
          title="Produits mis en avant"
          desc="Choisissez les produits affichés dans la sélection de l'accueil."
          enabled={fp.enabled}
          onToggle={() => setSection("featured_products", { enabled: !fp.enabled })}
          testid="home-section-products"
        >
          <div>
            <AiLabel ai={<AiTextButton kind="section_title" testid="home-products-title-ai-button" context={() => `Section produits mis en avant. Titre actuel : ${fp.title || "aucun"}`} onGenerated={(t) => setSection("featured_products", { title: t })} />}>Titre de la section</AiLabel>
            <input value={fp.title} onChange={(e) => setSection("featured_products", { title: e.target.value })} placeholder="Ex : Les pièces préférées de Dakar" className={inputClass} data-testid="home-products-title-input" />
          </div>
          <div>
            <label className={labelClass}>Ajouter un produit</label>
            <div className="flex gap-3">
              <select value={pickProduct} onChange={(e) => setPickProduct(e.target.value)} className={inputClass} data-testid="home-products-select">
                <option value="">Choisir un produit…</option>
                {availableProducts.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — {fcfa(p.price)}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid="home-products-add"
                disabled={!pickProduct}
                onClick={() => {
                  setSection("featured_products", { product_ids: [...fp.product_ids, pickProduct] });
                  setPickProduct("");
                }}
                className="flex h-11 shrink-0 items-center gap-2 rounded-full bg-bark px-6 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Ajouter
              </button>
            </div>
          </div>
          {selectedProducts.length > 0 && (
            <ul className="divide-y divide-clay border border-clay" data-testid="home-products-list">
              {selectedProducts.map((p) => (
                <li key={p.id} data-testid={`home-product-item-${p.id}`} className="flex items-center gap-3 bg-white px-4 py-2.5">
                  <div className="h-10 w-8 shrink-0 overflow-hidden border border-clay">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                  </div>
                  <p className="flex-1 truncate text-sm font-semibold text-bark">{p.name}</p>
                  <span className="text-xs text-bark/50">{fcfa(p.price)}</span>
                  <button
                    type="button"
                    data-testid={`home-product-remove-${p.id}`}
                    onClick={() => setSection("featured_products", { product_ids: fp.product_ids.filter((id) => id !== p.id) })}
                    className="text-bark/30 transition-colors hover:text-destructive"
                    aria-label={`Retirer ${p.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* CATEGORIES MISES EN AVANT */}
        <SectionCard
          title="Catégories mises en avant"
          desc="Les univers affichés en tuiles sur l'accueil, avec leur image."
          enabled={fc.enabled}
          onToggle={() => setSection("featured_categories", { enabled: !fc.enabled })}
          testid="home-section-categories"
        >
          <div>
            <AiLabel ai={<AiTextButton kind="section_title" testid="home-categories-title-ai-button" context={() => `Section catégories mises en avant. Titre actuel : ${fc.title || "aucun"}`} onGenerated={(t) => setSection("featured_categories", { title: t })} />}>Titre de la section</AiLabel>
            <input value={fc.title} onChange={(e) => setSection("featured_categories", { title: e.target.value })} placeholder="Ex : Nos univers" className={inputClass} data-testid="home-categories-title-input" />
          </div>
          {(fc.items || []).map((item, i) => (
            <div key={i} data-testid={`home-category-item-${i}`} className="space-y-3 border border-clay bg-white p-4">
              <div className="flex items-center gap-3">
                <select
                  value={item.name}
                  onChange={(e) => setSection("featured_categories", { items: fc.items.map((it, j) => (j === i ? { ...it, name: e.target.value } : it)) })}
                  className={inputClass}
                  data-testid={`home-category-name-${i}`}
                >
                  <option value="">Choisir une catégorie…</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid={`home-category-remove-${i}`}
                  onClick={() => setSection("featured_categories", { items: fc.items.filter((_, j) => j !== i) })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-destructive hover:text-destructive"
                  aria-label="Supprimer cette catégorie mise en avant"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input
                value={item.desc}
                onChange={(e) => setSection("featured_categories", { items: fc.items.map((it, j) => (j === i ? { ...it, desc: e.target.value } : it)) })}
                placeholder="Petite description (ex : Céramique & pierre naturelle)"
                className={inputClass}
                data-testid={`home-category-desc-${i}`}
              />
              <ImagePicker value={item.image} onChange={(url) => setSection("featured_categories", { items: fc.items.map((it, j) => (j === i ? { ...it, image: url } : it)) })} testid={`home-category-image-${i}`} />
            </div>
          ))}
          <button
            type="button"
            data-testid="home-categories-add"
            onClick={() => setSection("featured_categories", { items: [...(fc.items || []), { name: "", desc: "", image: "" }] })}
            className="flex h-11 items-center gap-2 rounded-full border border-terracotta px-6 text-xs font-bold uppercase tracking-[0.15em] text-terracotta transition-colors hover:bg-terracotta hover:text-sand"
          >
            <Plus className="h-4 w-4" /> Ajouter une catégorie
          </button>
        </SectionCard>

        {/* MANIFESTE */}
        <SectionCard
          title="Manifeste"
          desc="La section sombre avec votre texte et des chapitres numérotés."
          enabled={mf.enabled}
          onToggle={() => setSection("manifesto", { enabled: !mf.enabled })}
          testid="home-section-manifesto"
        >
          <div>
            <AiLabel ai={<AiTextButton kind="manifesto_title" testid="home-manifesto-title-ai-button" context={() => mf.title || ""} onGenerated={(t) => setSection("manifesto", { title: t })} />}>Titre</AiLabel>
            <input value={mf.title} onChange={(e) => setSection("manifesto", { title: e.target.value })} placeholder="Ex : « Sama keur, sa ker gi »" className={inputClass} data-testid="home-manifesto-title-input" />
          </div>
          <div>
            <AiLabel ai={<AiTextButton kind="manifesto_text" testid="home-manifesto-text-ai-button" context={() => mf.text || ""} onGenerated={(t) => setSection("manifesto", { text: t })} />}>Texte d'introduction</AiLabel>
            <textarea value={mf.text} onChange={(e) => setSection("manifesto", { text: e.target.value })} className="min-h-[80px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none" data-testid="home-manifesto-text-input" />
          </div>
          <div>
            <label className={labelClass}>Image (facultatif)</label>
            <ImagePicker value={mf.image} onChange={(url) => setSection("manifesto", { image: url })} testid="home-manifesto-image" />
          </div>
          <div>
            <label className={labelClass}>Chapitres (numérotés automatiquement)</label>
            <div className="space-y-3">
              {(mf.chapters || []).map((ch, i) => (
                <div key={i} data-testid={`home-chapter-${i}`} className="flex items-start gap-3 border border-clay bg-white p-4">
                  <div className="flex-1 space-y-2">
                    <input
                      value={ch.title}
                      onChange={(e) => setSection("manifesto", { chapters: mf.chapters.map((c, j) => (j === i ? { ...c, title: e.target.value } : c)) })}
                      placeholder="Titre du chapitre"
                      className={inputClass}
                      data-testid={`home-chapter-title-${i}`}
                    />
                    <textarea
                      value={ch.text}
                      onChange={(e) => setSection("manifesto", { chapters: mf.chapters.map((c, j) => (j === i ? { ...c, text: e.target.value } : c)) })}
                      placeholder="Texte du chapitre"
                      className="min-h-[60px] w-full rounded-md border border-clay bg-white px-3 py-2 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none"
                      data-testid={`home-chapter-text-${i}`}
                    />
                  </div>
                  <button
                    type="button"
                    data-testid={`home-chapter-remove-${i}`}
                    onClick={() => setSection("manifesto", { chapters: mf.chapters.filter((_, j) => j !== i) })}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-clay text-bark/40 transition-colors hover:border-destructive hover:text-destructive"
                    aria-label="Supprimer ce chapitre"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="home-chapters-add"
                onClick={() => setSection("manifesto", { chapters: [...(mf.chapters || []), { title: "", text: "" }] })}
                className="flex h-11 items-center gap-2 rounded-full border border-terracotta px-6 text-xs font-bold uppercase tracking-[0.15em] text-terracotta transition-colors hover:bg-terracotta hover:text-sand"
              >
                <Plus className="h-4 w-4" /> Ajouter un chapitre
              </button>
            </div>
          </div>
        </SectionCard>

        {/* BANDEAU CITATION */}
        <SectionCard
          title="Bandeau citation"
          desc="Une grande citation pleine largeur, avec ou sans image de fond."
          enabled={qb.enabled}
          onToggle={() => setSection("quote_band", { enabled: !qb.enabled })}
          testid="home-section-quote"
        >
          <div>
            <AiLabel ai={<AiTextButton kind="quote_text" testid="home-quote-text-ai-button" context={() => qb.text || ""} onGenerated={(t) => setSection("quote_band", { text: t })} />}>Citation</AiLabel>
            <textarea value={qb.text} onChange={(e) => setSection("quote_band", { text: e.target.value })} placeholder="Ex : Une belle maison n'est pas un luxe. C'est un art de vivre." className="min-h-[70px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none" data-testid="home-quote-text-input" />
          </div>
          <div>
            <label className={labelClass}>Signature (facultatif)</label>
            <input value={qb.author} onChange={(e) => setSection("quote_band", { author: e.target.value })} placeholder="Ex : L'équipe Sama-Keur, Dakar" className={inputClass} data-testid="home-quote-author-input" />
          </div>
          <div>
            <label className={labelClass}>Image de fond (facultatif)</label>
            <ImagePicker value={qb.image} onChange={(url) => setSection("quote_band", { image: url })} testid="home-quote-image" />
          </div>
        </SectionCard>

        {/* AVIS CLIENTS */}
        <SectionCard
          title="Avis clients en vedette"
          desc="Les 3 meilleurs avis approuvés s'affichent automatiquement sur l'accueil."
          enabled={rv.enabled}
          onToggle={() => setSection("reviews", { enabled: !rv.enabled })}
          testid="home-section-reviews"
        >
          <div>
            <AiLabel ai={<AiTextButton kind="section_title" testid="home-reviews-title-ai-button" context={() => `Section avis clients. Titre actuel : ${rv.title || "aucun"}`} onGenerated={(t) => setSection("reviews", { title: t })} />}>Titre de la section</AiLabel>
            <input value={rv.title || ""} onChange={(e) => setSection("reviews", { title: e.target.value })} placeholder="Ex : Ce que Dakar dit de nous" className={inputClass} data-testid="home-reviews-title-input" />
          </div>
          <p className="text-xs text-bark/40">
            La sélection est automatique : les 3 avis approuvés les mieux notés. Gérez les avis dans l'onglet « Avis ».
          </p>
        </SectionCard>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          data-testid="admin-home-save-bottom"
          onClick={save}
          disabled={saving}
          className="flex h-11 items-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Enregistrer la page d'accueil
        </button>
      </div>
    </section>
  );
};

export default HomeSettings;
